import { isLoginReturnSearch } from '@omaplus/shared-analytics-attribution';
import type { AnalyticsPageKey } from '@omaplus/shared-types';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigationType } from 'react-router';

import { useLanguage } from '../i18n/useLanguage.js';
import { authStatesEqual } from '../session/sessionMeMapping.js';
import { useSession } from '../session/useSession.js';
import type { AnalyticsCapturePort } from './AnalyticsCapturePort.js';
import { accountKeyForMember, useAnalyticsConsent } from './AnalyticsConsentProvider.js';
import { withoutBrowserEntryHandoff } from './browserAttribution.js';
import { type BrowserCapture, createBrowserCapture } from './browserCapture.js';
import { createCaptureController } from './captureController.js';

const ACTIVE_TICK_MS = 15_000;

interface AnalyticsRouteReadyContextValue {
  readonly eligible: boolean;
  readonly navigationEpoch: number;
  reportDisplayed(pageKey: AnalyticsPageKey): string | null;
  scheduleNoLongerDisplayed(navigationId: string): void;
}

const AnalyticsRouteReadyContext = createContext<AnalyticsRouteReadyContextValue | null>(null);

/**
 * Which facts of the document that loaded before the capture owner it may read.
 * A login-return document arrived from the identity provider, so its referrer
 * and query are not entry evidence, while its load timing remains observable.
 * A reloaded or history-restored document inherits the referrer and query of
 * an earlier load, so they are not entry evidence for it either.
 */
type DocumentEvidence = 'entryAndTiming' | 'timingOnly' | 'none';

interface CurrentNavigation {
  readonly id: string;
  readonly locationKey: string;
}

function documentLoadDuration(): number | undefined {
  const entry = performance.getEntriesByType('navigation')[0];
  if (entry === undefined) {
    return undefined;
  }
  const timing = entry as PerformanceNavigationTiming;
  if (!Number.isFinite(timing.loadEventEnd) || timing.loadEventEnd <= timing.startTime) {
    return undefined;
  }
  const duration = timing.loadEventEnd - timing.startTime;
  return Number.isFinite(duration) && duration >= 0 ? duration : undefined;
}

/**
 * True when Navigation Timing says this document repeats an earlier load
 * (`reload`, or a history traversal that was not restored from bfcache). An
 * unavailable entry or type keeps the ordinary classification.
 */
function documentRepeatsEarlierLoad(): boolean {
  const entry = performance.getEntriesByType('navigation')[0];
  if (entry === undefined || !('type' in entry)) {
    return false;
  }
  return entry.type === 'reload' || entry.type === 'back_forward';
}

function pageVisibility(): Readonly<{ visible: boolean; focused: boolean }> {
  return { visible: document.visibilityState === 'visible', focused: document.hasFocus() };
}

export interface AnalyticsCaptureProviderProps {
  readonly port: AnalyticsCapturePort;
  readonly children: ReactNode;
}

/**
 * Browser-only capture admission. It deliberately renders no capture owner at
 * all until the current server choice authorizes this member. That prevents
 * referrer, UTM, device, language, performance and listener setup from
 * happening before consent.
 */
export function AnalyticsCaptureProvider({
  port,
  children,
}: AnalyticsCaptureProviderProps): JSX.Element {
  const session = useSession();
  const consent = useAnalyticsConsent();
  const { captureRejected, mayReadDocumentEntry } = consent;
  const memberCaptureAllowed = session.state.kind === 'member' && consent.captureAllowed;
  const previousMemberAccountRef = useRef<string | null>(null);
  const [mayReadEntryAttribution, setMayReadEntryAttribution] = useState(true);
  // Read once per document, in the first render and before the cleanup below
  // removes the marker, so a later capture-owner remount cannot reclassify the
  // identity provider referrer as entry evidence.
  const [loginReturnDocument] = useState(() => isLoginReturnSearch(window.location.search));
  const [captureContext, setCaptureContext] = useState<AnalyticsRouteReadyContextValue | null>(
    null,
  );

  useEffect(() => {
    const accountKey = accountKeyForMember(session.state);
    if (accountKey === null) {
      return;
    }
    const previousAccountKey = previousMemberAccountRef.current;
    if (previousAccountKey !== null && previousAccountKey !== accountKey) {
      // A later member must start a distinct capture generation. Do not reuse
      // URL/referrer facts that may have arrived under the earlier account.
      setMayReadEntryAttribution(false);
    }
    previousMemberAccountRef.current = accountKey;
  }, [session.state]);

  // Security cleanup is independent of the analytics choice. It removes the
  // valueless login-return marker (or an old carried handoff) without reading,
  // classifying, or replaying anything.
  useLayoutEffect(() => {
    const search = withoutBrowserEntryHandoff(window.location.search);
    if (search !== null) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${search}${window.location.hash}`,
      );
    }
  }, []);

  const consentAwarePort = createConsentAwareCapturePort(port, captureRejected);
  const mayReadDocument = mayReadEntryAttribution && mayReadDocumentEntry;
  // The navigation type is read only once capture is allowed, so no Performance
  // API read happens before consent. It is fixed for the document's lifetime.
  const documentEvidence: DocumentEvidence = !mayReadDocument
    ? 'none'
    : loginReturnDocument || (memberCaptureAllowed && documentRepeatsEarlierLoad())
      ? 'timingOnly'
      : 'entryAndTiming';

  return (
    <AnalyticsRouteReadyContext.Provider value={memberCaptureAllowed ? captureContext : null}>
      {children}
      {memberCaptureAllowed && (
        <AcceptedAnalyticsCaptureOwner
          port={consentAwarePort}
          documentEvidence={documentEvidence}
          onContextChange={setCaptureContext}
        />
      )}
    </AnalyticsRouteReadyContext.Provider>
  );
}

function isConsentRequiredError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('analytics-consent-required');
}

function createConsentAwareCapturePort(
  port: AnalyticsCapturePort,
  onConsentRejected: () => void,
): AnalyticsCapturePort {
  const guard = async <T,>(operation: () => Promise<T>): Promise<T> => {
    try {
      return await operation();
    } catch (error: unknown) {
      if (isConsentRequiredError(error)) {
        onConsentRejected();
      }
      throw error;
    }
  };
  return {
    pageView: async (input) => await guard(() => port.pageView(input)),
    measure: async (input) => await guard(() => port.measure(input)),
    availability: async (input) => await guard(() => port.availability(input)),
  };
}

/**
 * Mounted only inside the accepted-current-consent branch above. The stable
 * sibling context provider keeps member UI state intact as consent changes;
 * this owner alone manages router and DOM measurement lifecycle effects.
 */
function AcceptedAnalyticsCaptureOwner({
  port,
  documentEvidence,
  onContextChange,
}: {
  readonly port: AnalyticsCapturePort;
  readonly documentEvidence: DocumentEvidence;
  readonly onContextChange: (context: AnalyticsRouteReadyContextValue | null) => void;
}): JSX.Element | null {
  const mayReadDocumentTiming = documentEvidence !== 'none';
  const location = useLocation();
  const navigationType = useNavigationType();
  const session = useSession();
  const language = useLanguage();
  const isMember = session.state.kind === 'member';
  // This code only mounts after an accepted choice is current. No carried
  // pre-login claim is used: authentication transitions cannot recover or
  // replay attribution collected before the member chose analytics.
  const [browser] = useState<BrowserCapture>(() =>
    createBrowserCapture({
      controller: createCaptureController({
        port,
        now: () => performance.now(),
        uuid: () => crypto.randomUUID(),
      }),
      now: () => performance.now(),
      userAgent: navigator.userAgent,
      language: language.active,
      entry:
        documentEvidence === 'entryAndTiming'
          ? {
              referrer: document.referrer,
              origin: window.location.origin,
              search: window.location.search,
            }
          : {},
    }),
  );
  const [navigationEpoch, setNavigationEpoch] = useState(0);
  const navigationRef = useRef<CurrentNavigation | null>(null);
  const serialRef = useRef(0);
  const firstNavigationRef = useRef(true);
  const previousSessionRef = useRef(session.state);
  const disposeTimerRef = useRef<number | null>(null);
  const departureTimersRef = useRef(new Map<string, number>());
  const mountedRef = useRef(false);
  const isMemberRef = useRef(isMember);

  // `pageshow` is an external browser event. Publish membership to its handler
  // only after this render commits, never from a speculative render pass.
  useLayoutEffect(() => {
    isMemberRef.current = isMember;
  }, [isMember]);

  useLayoutEffect(() => {
    const previousSession = previousSessionRef.current;
    const sessionChanged = !authStatesEqual(previousSession, session.state);
    previousSessionRef.current = session.state;
    if (sessionChanged && previousSession.kind === 'member') {
      browser.accountGenerationChanged();
      navigationRef.current = null;
    }
    if (!isMember) {
      return;
    }
    if (navigationRef.current?.locationKey === location.key) {
      return;
    }
    const navigationId = `member-navigation-${++serialRef.current}`;
    navigationRef.current = { id: navigationId, locationKey: location.key };
    const navigationKind = firstNavigationRef.current
      ? 'document'
      : navigationType === 'POP'
        ? 'history'
        : 'route';
    const documentDuration =
      navigationKind === 'document' && mayReadDocumentTiming ? documentLoadDuration() : undefined;
    browser.navigationStarted({
      navigationId,
      navigationKind,
      ...(documentDuration === undefined ? {} : { documentLoadDurationMs: documentDuration }),
    });
    setNavigationEpoch((epoch) => epoch + 1);
    firstNavigationRef.current = false;
  }, [browser, isMember, location.key, mayReadDocumentTiming, navigationType, session.state]);

  // react-compiler-exception: route-ready effects depend on this callback's identity for departure cleanup.
  const reportDisplayed = useCallback(
    (pageKey: AnalyticsPageKey): string | null => {
      if (!isMember) {
        return null;
      }
      const navigation = navigationRef.current;
      if (navigation !== null) {
        const departureTimer = departureTimersRef.current.get(navigation.id);
        if (departureTimer !== undefined) {
          window.clearTimeout(departureTimer);
          departureTimersRef.current.delete(navigation.id);
        }
        browser.pageDisplayed({ navigationId: navigation.id, pageKey, language: language.active });
        return navigation.id;
      }
      return null;
    },
    [browser, isMember, language.active],
  );

  // react-compiler-exception: route-ready effects depend on this callback's identity for departure cleanup.
  const scheduleNoLongerDisplayed = useCallback(
    (navigationId: string): void => {
      if (!mountedRef.current || departureTimersRef.current.has(navigationId)) {
        return;
      }
      const timer = window.setTimeout(() => {
        departureTimersRef.current.delete(navigationId);
        browser.pageNoLongerDisplayed(navigationId);
      }, 0);
      departureTimersRef.current.set(navigationId, timer);
    },
    [browser],
  );

  // This subscription owns browser-observable collection. A layout cleanup
  // removes it in the same withdrawal commit, before another external event
  // can reach the now-disallowed capture generation.
  useLayoutEffect(() => {
    mountedRef.current = true;
    if (disposeTimerRef.current !== null) {
      window.clearTimeout(disposeTimerRef.current);
      disposeTimerRef.current = null;
    }
    const updateVisibility = (): void => browser.visibilityChanged(pageVisibility());
    const observeError = (event: Event): void => {
      if (event instanceof ErrorEvent) {
        browser.errorObserved('unhandled');
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLImageElement ||
        target instanceof HTMLLinkElement ||
        target instanceof HTMLMediaElement ||
        target instanceof HTMLScriptElement
      ) {
        browser.errorObserved('resourceLoad');
      }
    };
    const observeUnhandledError = (): void => browser.errorObserved('unhandled');
    const closePage = (): void => browser.pageHidden();
    const restorePersistedPage = (event: PageTransitionEvent): void => {
      if (!event.persisted || !isMemberRef.current) {
        return;
      }
      const navigationId = `member-navigation-${++serialRef.current}`;
      navigationRef.current = { id: navigationId, locationKey: `bfcache-${navigationId}` };
      browser.navigationStarted({ navigationId, navigationKind: 'history' });
      setNavigationEpoch((epoch) => epoch + 1);
    };
    let documentLoadReadTimer: number | null = null;
    const observeDocumentLoad = (): void => {
      // `load` can fire before PerformanceNavigationTiming receives its final
      // `loadEventEnd`. Read on the next turn, then abandon the one-shot event
      // if the browser still has no completed value.
      documentLoadReadTimer = window.setTimeout(() => {
        documentLoadReadTimer = null;
        const duration = documentLoadDuration();
        if (duration !== undefined) {
          browser.documentLoadObserved(duration);
        }
      }, 0);
    };
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', updateVisibility);
    window.addEventListener('blur', updateVisibility);
    window.addEventListener('pagehide', closePage);
    window.addEventListener('pageshow', restorePersistedPage);
    window.addEventListener('error', observeError, true);
    window.addEventListener('unhandledrejection', observeUnhandledError);
    if (mayReadDocumentTiming) {
      window.addEventListener('load', observeDocumentLoad, { once: true });
    }
    const interval = window.setInterval(() => browser.tick(), ACTIVE_TICK_MS);
    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', updateVisibility);
      window.removeEventListener('blur', updateVisibility);
      window.removeEventListener('pagehide', closePage);
      window.removeEventListener('pageshow', restorePersistedPage);
      window.removeEventListener('error', observeError, true);
      window.removeEventListener('unhandledrejection', observeUnhandledError);
      if (mayReadDocumentTiming) {
        window.removeEventListener('load', observeDocumentLoad);
      }
      window.clearInterval(interval);
      if (documentLoadReadTimer !== null) {
        window.clearTimeout(documentLoadReadTimer);
      }
      for (const timer of departureTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      departureTimersRef.current.clear();
      // StrictMode immediately re-runs the effect. Deferring disposal by one
      // task preserves the same logical navigation through that probe while a
      // real unmount still clears the in-memory controller state.
      disposeTimerRef.current = window.setTimeout(() => browser.dispose(), 0);
    };
  }, [browser, mayReadDocumentTiming]);

  // react-compiler-exception: route-ready effects use the provider value as their lifecycle contract.
  const context = useMemo<AnalyticsRouteReadyContextValue>(
    () => ({ eligible: isMember, navigationEpoch, reportDisplayed, scheduleNoLongerDisplayed }),
    [isMember, navigationEpoch, reportDisplayed, scheduleNoLongerDisplayed],
  );

  useLayoutEffect(() => {
    onContextChange(context);
    return () => onContextChange(null);
  }, [context, onContextChange]);

  return null;
}

/** Report an already-rendered eligible route without exposing router or DOM mechanics. */
export function useAnalyticsRouteReady(pageKey: AnalyticsPageKey): void {
  const context = useContext(AnalyticsRouteReadyContext);
  useEffect(() => {
    if (!context?.eligible) {
      return;
    }
    const navigationId = context.reportDisplayed(pageKey);
    if (navigationId === null) {
      return;
    }
    return () => context.scheduleNoLongerDisplayed(navigationId);
  }, [context, pageKey]);
}

/** Declarative route-success signal for RouteRegistry's admitted member surfaces. */
export function AnalyticsRouteReady({ pageKey }: { readonly pageKey: AnalyticsPageKey }): null {
  useAnalyticsRouteReady(pageKey);
  return null;
}
